import { useFindMany } from "@gadgetinc/react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  PlusIcon,
} from "@heroicons/react/16/solid";
import {
  CheckIcon,
  PencilIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../externalComponents/tailwind/dialog";
import {
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router";
import { api } from "../../api";
import { OverflowFrame, PageHeading } from "../../externalComponents";
import { ChipsContactFilter } from "../../externalComponents/filters";
import TagContactsPopup from "../../externalComponents/filters/TagContactsPopup";
import LoadingSpinner from "../../externalComponents/LoadingSpinner";
import SegmentsSidebar from "../../externalComponents/SegmentsSidebar";
import DialogDeleteSegment from "../../externalComponents/DialogDeleteSegment";
import { DateTooltip } from "../../externalComponents/DateTooltip";
import { Badge } from "../../externalComponents/tailwind/badge";
import { Button } from "../../externalComponents/tailwind/button";
import { Input } from "../../externalComponents/tailwind/input";
import { Divider } from "../../externalComponents/tailwind/divider";
import EmptyState from "../../externalComponents/tailwind/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../externalComponents/tailwind/table";
import { useIsMobile } from "../../externalComponents/useIsMobile";
import { CustomerContext } from "../../providers";
import { ContactsActionsDropdown } from "./shared";
import { buildUIFilters } from "./utils/filterUtils";
import { getExportActions } from "./utils/exportUtils";
import {
  createPaginationHandler,
  createNextPageHandler,
  createPreviousPageHandler,
} from "./utils/paginationUtils";
import { createTaggingHandlers } from "./utils/taggingUtils";
import {
  transformContactData,
  getDefaultFields,
} from "./utils/dataTransformationUtils";
import { decodeFiltersFromUrl } from "./utils/filterUrlUtils";

const ContactsPage = () => {
  const { user, customer, integrations } = useContext(CustomerContext);
  const isMobile = useIsMobile();
  const location = useLocation();

  const NUM_ON_PAGE = 10;
  const navigate = useNavigate();
  const _defaultFields = getDefaultFields();

  // Helper function to update selected segment and URL parameter
  const updateSelectedSegment = useCallback(
    (segment) => {
      setSelectedSegment(segment);

      // Update URL parameter
      const searchParams = new URLSearchParams(location.search);
      if (segment.id === "all-contacts") {
        searchParams.delete("segment");
      } else {
        searchParams.set("segment", segment.id);
      }

      const newSearch = searchParams.toString();
      const newUrl = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname;
      navigate(newUrl, { replace: true });
    },
    [location.search, location.pathname, navigate],
  );

  const [cursor, setCursor] = useState({ first: NUM_ON_PAGE });
  const [pageNumber, setPageNumber] = useState(1);
  const [filterGroups, setFilterGroups] = useState([]); // AND groups of OR phone equals
  const [selectedSegment, setSelectedSegment] = useState({
    id: "all-contacts",
    name: "All contacts",
    lastContactsCount: null,
    lastRefreshedAt: null,
    type: "default",
    source: "simio",
    filters: null,
  }); // Selected segment for filtering

  // Editing state for segment name
  const [isEditingSegmentName, setIsEditingSegmentName] = useState(false);
  const [segmentName, setSegmentName] = useState("");

  // Delete segment dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRenameLoading, setIsRenameLoading] = useState(false);
  const inputRef = useRef(null);

  // Save segment popup state
  const [showSaveSegmentPopup, setShowSaveSegmentPopup] = useState(false);
  const [saveSegmentOption, setSaveSegmentOption] = useState("save-current"); // "save-current" or "create-new"
  const [newSegmentName, setNewSegmentName] = useState("");
  const [isSaveSegmentLoading, setIsSaveSegmentLoading] = useState(false);

  // Contact count from ContactCount component
  const [currentContactCount, setCurrentContactCount] = useState(0);

  // Sidebar visibility state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Export actions based on filters
  const allowedActions = useMemo(() => {
    return getExportActions(user?.email, customer, filterGroups);
  }, [user, customer, filterGroups]);

  // Fetch recent campaigns for AI context
  const [{ data: recentCampaigns }] = useFindMany(api.campaign, {
    filter: {
      customerId: { equals: customer.id },
      isArchived: { notEquals: true },
      trigger: { equals: "manual" },
      sendings: {
        some: {
          status: { equals: "completed" },
        },
      },
    },
    sort: {
      updatedAt: "Descending",
    },
    select: {
      id: true,
      name: true,
      scheduledDate: true,
    },
    first: 10,
  });

  // Fetch active automations for AI context
  const [{ data: activeAutomations }] = useFindMany(api.campaign, {
    filter: {
      customerId: { equals: customer.id },
      isArchived: { notEquals: true },
      trigger: { notEquals: "manual" },
      isAutomationActive: { equals: true },
    },
    sort: {
      updatedAt: "Descending",
    },
    select: {
      id: true,
      name: true,
      automationType: true,
      scheduledDate: true,
    },
    first: 10,
  });

  // Fetch chatbots for AI context
  const [{ data: chatbots }] = useFindMany(api.scenario, {
    filter: {
      customerId: { equals: customer.id },
      isArchived: { notEquals: true },
      type: { equals: "chatbot" },
    },
    sort: {
      updatedAt: "Descending",
    },
    select: {
      id: true,
      name: true,
    },
    first: 10,
  });

  // Tagging state
  const [showTagPopup, setShowTagPopup] = useState(false);
  const [taggingContactCount, setTaggingContactCount] = useState(0);
  const [taggingFilterGroups, setTaggingFilterGroups] = useState([]);
  const [isTagging, setIsTagging] = useState(false);

  const [criteria, setCriteria] = useState({
    ...cursor,
    filter: [{ customerId: { equals: user.customerId } }],
  });

  // Build additional filters from UI groups (AND of ORs)
  const uiFilters = useMemo(() => {
    return buildUIFilters(filterGroups);
  }, [filterGroups]);

  // Build segment filters
  const segmentFilters = useMemo(() => {
    if (!selectedSegment || selectedSegment.id === "all-contacts") {
      return [];
    }

    // Don't apply segment filters if the user has modified the filters
    // This prevents double-filtering when user modifies filters after selecting a segment
    if (filterGroups.length > 0) {
      return [];
    }

    // Segment filters are in UI format, so convert them to API format
    if (selectedSegment.filters && selectedSegment.filters.length > 0) {
      return buildUIFilters(selectedSegment.filters);
    }

    return [];
  }, [selectedSegment, filterGroups]);

  const combinedCriteria = useMemo(() => {
    const base = { ...criteria };
    const baseFilterArray = Array.isArray(base.filter)
      ? base.filter
      : [base.filter].filter(Boolean);
    const filter = [...baseFilterArray, ...uiFilters, ...segmentFilters];
    return { ...base, filter };
  }, [criteria, uiFilters, segmentFilters]);

  const [{ data, fetching }, refetch] = useFindMany(api.contact, {
    ...combinedCriteria,
    select: _defaultFields,
    sort: {
      updatedAt: "Descending",
    },
  });

  const changePage = useCallback(
    createPaginationHandler(setCursor, setPageNumber, setCriteria, user),
    [],
  );

  const getNextPage = useCallback(
    createNextPageHandler(data, changePage, pageNumber, NUM_ON_PAGE),
    [data, changePage, pageNumber, NUM_ON_PAGE],
  );

  const getPreviousPage = useCallback(
    createPreviousPageHandler(pageNumber, changePage, data, NUM_ON_PAGE),
    [pageNumber, changePage, data, NUM_ON_PAGE],
  );

  const dataSource = useMemo(() => {
    return transformContactData(data);
  }, [data]);

  // Tagging handlers
  const { handleAddTag, handleTagContacts, handleCloseTagPopup } = useMemo(
    () =>
      createTaggingHandlers(
        setTaggingFilterGroups,
        setTaggingContactCount,
        setShowTagPopup,
        setIsTagging,
        customer,
        refetch,
      ),
    [customer, refetch],
  );

  // Create a wrapper for handleTagContacts that includes the state
  const handleTagContactsWithState = useCallback(
    (selectedTags) => {
      return handleTagContacts(
        selectedTags,
        taggingFilterGroups,
        taggingContactCount,
      );
    },
    [handleTagContacts, taggingFilterGroups, taggingContactCount],
  );

  // Handle segment selection
  const handleSegmentSelect = useCallback(
    (segment) => {
      updateSelectedSegment(segment);

      // Use segment filters directly as UI filter groups (already in correct format)
      if (segment.filters && segment.filters.length > 0) {
        setFilterGroups(segment.filters);
      } else {
        setFilterGroups([]);
      }

      // Close editing mode when switching segments
      setIsEditingSegmentName(false);

      // Reset pagination when segment changes
      setCursor({ first: NUM_ON_PAGE });
      setPageNumber(1);

      // Clear filters URL parameter when selecting a segment
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.has("filters")) {
        searchParams.delete("filters");
        const newUrl = searchParams.toString()
          ? `${location.pathname}?${searchParams.toString()}`
          : location.pathname;
        navigate(newUrl, { replace: true });
      }
    },
    [
      NUM_ON_PAGE,
      updateSelectedSegment,
      location.search,
      location.pathname,
      navigate,
    ],
  );

  useEffect(() => {
    if (isEditingSegmentName && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditingSegmentName]);

  // Handle URL parameter for segment filtering and filters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const segmentId = searchParams.get("segment");
    const filtersParam = searchParams.get("filters");

    if (segmentId && segmentId !== selectedSegment.id) {
      // Find the segment in the available segments and set it as selected
      const findAndSetSegment = async () => {
        try {
          const segment = await api.segment.findOne(segmentId, {
            filter: {
              isArchived: { notEquals: true },
            },
            select: {
              id: true,
              name: true,
              lastContactsCount: true,
              lastRefreshedAt: true,
              type: true,
              source: true,
              filters: true,
            },
          });

          if (segment) {
            setSelectedSegment(segment);
            // Apply segment filters
            if (segment.filters && segment.filters.length > 0) {
              setFilterGroups(segment.filters);
            } else {
              setFilterGroups([]);
            }
            // Reset pagination when segment changes
            setCursor({ first: NUM_ON_PAGE });
            setPageNumber(1);
          }
        } catch (error) {
          console.error("Error fetching segment:", error);
        }
      };

      findAndSetSegment();
    } else if (!segmentId && selectedSegment.id !== "all-contacts") {
      // If no segment in URL but we have a selected segment, reset to all contacts
      setSelectedSegment({
        id: "all-contacts",
        name: "All contacts",
        lastContactsCount: null,
        lastRefreshedAt: null,
        type: "default",
        source: "simio",
        filters: null,
      });
      setFilterGroups([]);
      setCursor({ first: NUM_ON_PAGE });
      setPageNumber(1);
    }

    // Handle filters parameter (only if no segment parameter is given)
    if (!segmentId && filtersParam) {
      try {
        const decodedFilters = decodeFiltersFromUrl(filtersParam);
        if (decodedFilters && decodedFilters.length > 0) {
          // Set to all-contacts segment and apply the filters
          setSelectedSegment({
            id: "all-contacts",
            name: "All contacts",
            lastContactsCount: null,
            lastRefreshedAt: null,
            type: "default",
            source: "simio",
            filters: null,
          });
          setFilterGroups(decodedFilters);
          // Reset pagination when filters change
          setCursor({ first: NUM_ON_PAGE });
          setPageNumber(1);
        }
      } catch (error) {
        console.error("Error decoding filters from URL:", error);
      }
    }
  }, [location.search, NUM_ON_PAGE, selectedSegment.id]);

  // Handle click outside to cancel editing
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isEditingSegmentName &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        handleCancelRename();
      }
    };

    if (isEditingSegmentName) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditingSegmentName]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      setSegmentName(e.target.value);
      handleRenameSegment();
    } else if (e.key === "Escape") {
      handleCancelRename();
    }
  };

  const handleCancelRename = () => {
    setIsEditingSegmentName(false);
    setSegmentName("");
  };

  const handleRenameSegment = async () => {
    setIsRenameLoading(true);
    if (selectedSegment.id === "all-contacts") {
      // Don't rename "All contacts"
      setIsEditingSegmentName(false);
      setIsRenameLoading(false);
      return;
    }

    await api.segment.update(selectedSegment.id, {
      name: segmentName,
    });
    // Update the selectedSegment state with the new name
    setSelectedSegment((prev) => ({ ...prev, name: segmentName }));
    setIsEditingSegmentName(false);
    setIsRenameLoading(false);
  };

  // Handle save segment popup
  const handleSaveSegmentClick = () => {
    setShowSaveSegmentPopup(true);
    // If "All contacts" is selected, default to "create-new" since we can't save to it
    if (selectedSegment && selectedSegment.id === "all-contacts") {
      setSaveSegmentOption("create-new");
    } else {
      setSaveSegmentOption("save-current");
    }
    setNewSegmentName("");
  };

  const handleSaveSegment = async () => {
    setIsSaveSegmentLoading(true);

    try {
      if (saveSegmentOption === "save-current") {
        // Update current segment with new filters
        if (selectedSegment.id !== "all-contacts") {
          await api.segment.update(selectedSegment.id, {
            filters: filterGroups,
            lastContactsCount: currentContactCount,
            lastRefreshedAt: new Date(),
          });

          // Update segment membership based on new filters (don't await - can take minutes)
          api.updateSegmentMembershipBySegment({
            segmentId: selectedSegment.id,
          });

          // Update the selectedSegment state with new filters
          setSelectedSegment((prev) => ({ ...prev, filters: filterGroups }));
        }
      } else {
        // Create new segment
        if (newSegmentName.trim()) {
          const newSegment = await api.segment.create({
            name: newSegmentName.trim(),
            customer: { _link: customer.id },
            filters: filterGroups,
            type: "materialized",
            source: "simio",
            lastContactsCount: currentContactCount,
            lastRefreshedAt: new Date(),
          });

          // Update segment membership based on filters (don't await - can take minutes)
          api.updateSegmentMembershipBySegment({
            segmentId: newSegment.id,
          });

          // Switch to the new segment
          updateSelectedSegment(newSegment);
        }
      }

      setShowSaveSegmentPopup(false);
    } catch (error) {
      console.error("Error saving segment:", error);
    } finally {
      setIsSaveSegmentLoading(false);
    }
  };

  const handleCloseSaveSegmentPopup = () => {
    setShowSaveSegmentPopup(false);
    setSaveSegmentOption("save-current");
    setNewSegmentName("");
  };

  const handleDeleteSegment = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteSegmentSuccess = (deletedSegment) => {
    // Reset to "All contacts" if the deleted segment was selected
    if (selectedSegment.id === deletedSegment.id) {
      setSelectedSegment({
        id: "all-contacts",
        name: "All contacts",
        lastContactsCount: null,
        lastRefreshedAt: null,
        type: "default",
        source: "simio",
        filters: null,
      });
      setFilterGroups([]);
      setCursor({ first: NUM_ON_PAGE });
      setPageNumber(1);

      // Remove the segment parameter from the URL
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete("segment");
      const newUrl = searchParams.toString()
        ? `${location.pathname}?${searchParams.toString()}`
        : location.pathname;
      navigate(newUrl, { replace: true });
    }
  };

  return (
    <>
      <PageHeading
        title="Contacts"
        {...(!isMobile && {
          primaryAction: "Import",
          handlePrimaryAction: async () => navigate("/contacts/import"),
          dropdownButton: true,
          dropdownItems: allowedActions,
        })}
      />
      <div className="flex h-[calc(100vh-7em)]">
        {!isMobile && !isSidebarCollapsed && (
          <SegmentsSidebar
            selectedSegment={selectedSegment}
            onSegmentSelect={handleSegmentSelect}
          />
        )}
        <div className="flex-1">
          <OverflowFrame>
            <>
              {selectedSegment && (
                <div className="py-3 border-b border-gray-200">
                  <div className="flex items-center">
                    {isEditingSegmentName ? (
                      <div className="flex items-center gap-2 w-full">
                        <Input
                          ref={inputRef}
                          className="font-title font-bold leading-7 text-gray-900 dark:text-white"
                          style={{ fontSize: "20px" }}
                          value={segmentName}
                          onChange={(e) => {
                            setSegmentName(e.target.value);
                          }}
                          autoComplete="off"
                          onKeyUp={(e) => {
                            handleKeyPress(e);
                          }}
                        />
                        <Button
                          outline
                          className="h-8 w-8 p-0"
                          onClick={() => {
                            handleRenameSegment();
                          }}
                          loading={isRenameLoading}
                        >
                          <CheckIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center w-full">
                        {/* Collapse/Expand Sidebar Button */}
                        <button
                          onClick={() =>
                            setIsSidebarCollapsed(!isSidebarCollapsed)
                          }
                          className="p-1 rounded-md hover:bg-gray-100 transition-colors mr-3"
                          title={
                            isSidebarCollapsed
                              ? "Show segments sidebar"
                              : "Hide segments sidebar"
                          }
                        >
                          {isSidebarCollapsed ? (
                            <ChevronRightIcon className="h-5 w-5 text-gray-500 hover:text-gray-700" />
                          ) : (
                            <ChevronLeftIcon className="h-5 w-5 text-gray-500 hover:text-gray-700" />
                          )}
                        </button>

                        {/* Segment Name */}
                        <div
                          className={`flex items-center gap-2 flex-1 ${
                            selectedSegment.id !== "all-contacts"
                              ? "cursor-pointer hover:text-gray-700 group"
                              : "cursor-default"
                          }`}
                          onClick={() => {
                            if (selectedSegment.id !== "all-contacts") {
                              setSegmentName(selectedSegment.name);
                              setIsEditingSegmentName(true);
                            }
                          }}
                          title={
                            selectedSegment.id !== "all-contacts"
                              ? "Click to edit name"
                              : ""
                          }
                        >
                          <span className="text-xl font-semibold text-gray-900">
                            {selectedSegment.name}
                          </span>
                          {selectedSegment.id !== "all-contacts" && (
                            <PencilIcon className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                          )}
                        </div>

                        {/* Delete Button */}
                        {selectedSegment.id !== "all-contacts" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSegment();
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors ml-3"
                            title="Delete segment"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <ChipsContactFilter
                groups={filterGroups}
                setGroups={setFilterGroups}
                customerId={customer.id}
                customFields={customer.customFields}
                onAddTag={handleAddTag}
                recentCampaigns={recentCampaigns}
                activeAutomations={activeAutomations}
                chatbots={chatbots}
                integrations={integrations}
                onSaveSegmentClick={handleSaveSegmentClick}
                onContactCountChange={setCurrentContactCount}
              />
              <Divider />
              {fetching ? (
                <LoadingSpinner />
              ) : dataSource && dataSource.length > 0 ? (
                <Table className="mx-3 md:mx-0">
                  <TableHead>
                    <TableRow>
                      <TableHeader
                        className={`${isMobile ? "w-[45%] break-words" : ""}`}
                      >
                        Phone
                      </TableHeader>
                      <TableHeader className="w-[20%]">Full name</TableHeader>
                      {!isMobile && <TableHeader>Created</TableHeader>}
                      {!isMobile && <TableHeader>Last updated</TableHeader>}
                      <TableHeader>Optin status</TableHeader>
                      {!isMobile && (
                        <TableHeader className="text-center">
                          Actions
                        </TableHeader>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataSource.map((row) => (
                      <TableRow key={row.key} href={`/contacts/${row.id}`}>
                        <TableCell
                          className={`${isMobile ? "w-[45%] break-all" : ""}`}
                        >
                          {row.phoneNumber && row.phoneNumber.length > 15
                            ? `${row.phoneNumber.substring(0, 15)}...`
                            : row.phoneNumber}
                        </TableCell>
                        <TableCell className="w-[20%]">
                          {row.name && row.name.length > 30
                            ? `${row.name.substring(0, 30)}...`
                            : row.name}
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <DateTooltip
                              date={row.createdAt}
                              originalDate={row.createdAtOriginal}
                              onClick={() => navigate(`/contacts/${row.id}`)}
                            />
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell>
                            <DateTooltip
                              date={row.updatedAt}
                              originalDate={row.updatedAtOriginal}
                              onClick={() => navigate(`/contacts/${row.id}`)}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge color={row.optinColor}>{row.optinLabel}</Badge>
                        </TableCell>
                        {!isMobile && (
                          <TableCell className="text-center">
                            <div className="-mx-3 -my-1.5 sm:-mx-2.5">
                              <ContactsActionsDropdown
                                customerId={customer.id}
                                contactId={row.id}
                              />
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <EmptyState
                  heading="You have no contacts"
                  action={async () => {
                    navigate("/contacts/import");
                  }}
                  icon={<PlusIcon />}
                  actionText="Import contacts"
                  img="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                />
              )}

              <div className="flex justify-start my-4">
                <Button
                  color="secondaryButton"
                  onClick={getPreviousPage}
                  disabled={!data?.hasPreviousPage}
                >
                  <ArrowLeftIcon />
                  Previous
                </Button>
                <Badge className="my-1 mx-2 min-w-10 flex items-center justify-center">
                  {pageNumber}
                </Badge>
                <Button
                  color="secondaryButton"
                  onClick={getNextPage}
                  disabled={!data?.hasNextPage}
                >
                  Next
                  <ArrowRightIcon />
                </Button>
              </div>
            </>
          </OverflowFrame>
        </div>
      </div>

      {/* Tag Contacts Popup */}
      <TagContactsPopup
        isOpen={showTagPopup}
        onClose={handleCloseTagPopup}
        contactCount={taggingContactCount}
        onTagContacts={handleTagContactsWithState}
        customerId={customer.id}
        isLoading={isTagging}
      />

      {/* Save Segment Popup */}
      <Dialog open={showSaveSegmentPopup} onClose={handleCloseSaveSegmentPopup}>
        <DialogTitle>Save segment</DialogTitle>
        <DialogBody>
          <div className="space-y-4">
            <div className="space-y-3">
              <label
                className={`flex items-center space-x-3 ${selectedSegment && selectedSegment.id === "all-contacts" ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
              >
                <input
                  type="radio"
                  name="saveOption"
                  value="save-current"
                  checked={saveSegmentOption === "save-current"}
                  onChange={(e) => setSaveSegmentOption(e.target.value)}
                  disabled={
                    selectedSegment && selectedSegment.id === "all-contacts"
                  }
                  className="h-4 w-4 text-pink focus:ring-pink border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <span
                  className={`text-sm font-medium ${selectedSegment && selectedSegment.id === "all-contacts" ? "text-gray-500" : "text-gray-900"}`}
                >
                  Save changes to "{selectedSegment.name}"
                </span>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="saveOption"
                  value="create-new"
                  checked={saveSegmentOption === "create-new"}
                  onChange={(e) => setSaveSegmentOption(e.target.value)}
                  className="h-4 w-4 text-pink focus:ring-pink border-gray-300"
                />
                <span className="text-sm font-medium text-gray-900">
                  Create new segment
                </span>
              </label>
            </div>

            {saveSegmentOption === "create-new" && (
              <div className="mt-4">
                <label
                  htmlFor="newSegmentName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Segment name
                </label>
                <Input
                  id="newSegmentName"
                  value={newSegmentName}
                  onChange={(e) => setNewSegmentName(e.target.value)}
                  placeholder="Enter segment name"
                  autoComplete="off"
                />
              </div>
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <Button
            color="secondaryButton"
            onClick={handleCloseSaveSegmentPopup}
            disabled={isSaveSegmentLoading}
          >
            Cancel
          </Button>
          <Button
            color="primaryButton"
            onClick={handleSaveSegment}
            loading={isSaveSegmentLoading}
            disabled={
              saveSegmentOption === "create-new" && !newSegmentName.trim()
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Segment Dialog */}
      <DialogDeleteSegment
        segment={selectedSegment.id !== "all-contacts" ? selectedSegment : null}
        isModalOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onSuccess={handleDeleteSegmentSuccess}
      />
    </>
  );
};

export default ContactsPage;
